


























































































import Vue from 'vue';
import { throttle, get } from 'lodash';
import tableFactory from '@/core/store/modules/tableFactory';
import { SET_QUERY, SET_PER_PAGE_FIXED } from '@/core/store/mutation-types';

interface Field {
  // use name when you need to use the slot
  name?: string;
  title?: string;
  // use key when you just want to render the text value of object property
  key?: string;
  cssClass?: string;
  ordering?: string;
  // don't call item-click event on td
  disableClick?: boolean;
}

export default Vue.extend({
  props: {
    name: {
      type: String,
      required: true,
    },
    fields: {
      type: Array as () => Array<Field>,
      default() {
        return [];
      },
    },
    apiMethod: {
      type: Function,
      default: null,
    },
    api: {
      type: String,
      default: null,
    },
    query: {
      type: Object,
      default() {
        return {};
      },
    },
    rowsClickable: {
      type: Boolean,
      default: false,
    },
    showPaginationButtons: {
      type: Boolean,
      default: true,
    },
    pageInQuery: {
      type: Boolean,
      default: false,
    },
    perPageFixed: {
      type: Number,
      required: false,
    },
    disableHover: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      paginationLimit: 1,
      firstLoad: true,
      hoverIndex: null,
    };
  },
  methods: {
    getOrderingIcons(ordering) {
      switch (this.orderingDirection + ordering) {
        case this.ordering:
          return ['fas', 'sort-up'];
        case `-${this.ordering}`:
          return ['fas', 'sort-down'];
        default:
          return ['fas', 'sort'];
      }
    },
    async reload(page = 1) {
      const res = await this.$store.dispatch(`${this.name}/reload`, page);
      return res;
    },
    refresh() {
      return this.$store.dispatch(`${this.name}/refresh`);
    },
    orderingBy(ordering) {
      return this.$store.dispatch(`${this.name}/orderingBy`, ordering);
    },
    setQuery() {
      this.$store.dispatch(`${this.name}/setQuery`, this.query);
      if (this.pageInQuery && this.$route.query.page) {
        this.$router.push({ query: {} });
      }
    },
    onWindowResize: throttle(function () {
      this.updatePaginationLimit();
    }, 300),
    updatePaginationLimit() {
      const paginatedTable = this.$refs['paginated-table'];
      if (paginatedTable) {
        const tableWidth = paginatedTable.clientWidth;
        this.paginationLimit = [
          { width: 500, limit: 3 },
          { width: 800, limit: 5 },
          { width: 1000, limit: 9 },
        ].reduce((limit, item) => {
          return item.width > tableWidth ? limit : item.limit;
        }, 1);
      }
    },
    getCssClass(cellData) {
      const cssClass = cellData.cssClass?.split(' ') || '';
      if (cellData.disableClick) cssClass.push('disable-click');
      return cssClass.join(' ');
    },
    setHoverIndex(rowIndex, cellData) {
      if (!cellData.disableClick) {
        this.hoverIndex = rowIndex;
      }
    },
    getPageQuery(page) {
      // Preserve all existing query parameters
      const currentQuery = { ...this.$route.query };
      if (page > 1) {
        return { ...currentQuery, page };
      } else {
        // Remove page param but keep all others
        const { page: _, ...rest } = currentQuery;
        return rest;
      }
    },
    getPageFromQuery() {
      return parseInt(this.$route.query.page || 1);
    },
    onRouteChange() {
      if (!this.pageInQuery) return;
      const page = this.getPageFromQuery();
      if (page != this.currentPage) {
        this.$store.dispatch(`${this.name}/onPageSelect`, page);
      }
    },
  },
  computed: {
    res() {
      return this.$store.state[this.name].res;
    },
    isLoading() {
      return this.$store.state[this.name].isLoading;
    },
    isNoData() {
      return this.$store.getters[`${this.name}/isNoData`];
    },
    isNoDataForQuery() {
      return this.$store.getters[`${this.name}/isNoDataForQuery`];
    },
    currentPage: {
      get() {
        return this.$store.state[this.name].currentPage;
      },
      set(currentPage) {
        const routePage = this.$route.query.page || 1;
        if (this.pageInQuery) {
          if (routePage != currentPage) {
            const routeName = this.$route.name || undefined;
            this.$router.push({
              name: routeName,
              query: this.getPageQuery(currentPage),
            });
          }
        } else {
          this.$store.dispatch(`${this.name}/onPageSelect`, currentPage);
        }
      },
    },
    perPage() {
      return this.$store.state[this.name].perPage;
    },
    allPages() {
      return this.$store.state[this.name].allPages;
    },
    ordering() {
      return this.$store.state[this.name].ordering;
    },
    orderingDirection() {
      return this.$store.state[this.name].orderingDirection;
    },
    showPagination() {
      return this.$store.getters[`${this.name}/showPagination`];
    },
    tableData() {
      if (this.isLoading) return [];

      const tableData = this.res.results.map(result => {
        return this.fields.map((field, fieldIndex) => {
          return {
            cssClass: field.cssClass || '',
            slotData: result,
            slotName: field.name || fieldIndex,
            text: field.key ? get(result, field.key) : null,
            disableClick: field.disableClick,
          };
        });
      });

      return tableData;
    },
  },
  watch: {
    query: 'setQuery',
    orderingParam: 'setOrderingParam',
    $route: 'onRouteChange',
  },
  async mounted() {
    await this.$store.commit(
      `${this.name}/${SET_PER_PAGE_FIXED}`,
      this.perPageFixed
    );
    await this.$store.commit(`${this.name}/${SET_QUERY}`, this.query);

    let initialPage = 1;
    if (this.pageInQuery) {
      initialPage = this.getPageFromQuery();
    }
    try {
      await this.reload(initialPage);
    } catch (e) {
      // noop
    }

    this.firstLoad = false;
    window.addEventListener('resize', this.onWindowResize);
  },
  created() {
    if (!this.$store.hasModule(this.name)) {
      const tableState = tableFactory();
      this.$store.registerModule(this.name, tableState);
    }
    if (this.apiMethod) {
      this.$store.dispatch(`${this.name}/setRequestDispatch`, this.apiMethod);
    } else {
      this.$store.dispatch(`${this.name}/setRequestDispatch`, this.api);
    }
  },
  updated: function () {
    this.$nextTick(() => {
      this.updatePaginationLimit();
    });
  },
  destroyed() {
    window.removeEventListener('resize', this.onWindowResize);
  },
});
